import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import MaterialDialog from '@material-ui/core/Dialog'
import MaterialDialogActions from '@material-ui/core/DialogActions'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from 'components/material-symbol/icons/Close'

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    zIndex: 1,
    color: theme.palette.text.secondary,

    [theme.breakpoints.down('xs')]: {
      top: theme.spacing(1),
      right: theme.spacing(1),
    },

    '& .MuiSvgIcon-root': {
      width: '24px',
      height: '24px',
      fontSize: '24px',

      [theme.breakpoints.down('xs')]: {
        width: '20px',
        height: '20px',
        fontSize: '20px',
      },
    },
  },
}))

const Dialog = props => {
  const {
    children,
    trigger,
    isOpen,
    showCloseButton,
    disableClose = false,
    fullWidth,
    fullScreen,
    maxWidth,
    onClose,
    beforeOpen,
    hideBackdrop,
    classes,
    disableEnforceFocus = false,
    scroll,
    'aria-labelledby': ariaLabelledBy,
  } = props

  const styles = useStyles()

  const [_isOpen, _setIsOpen] = useState(false)
  const openDialog = async () => {
    if (beforeOpen) {
      const proceed = await beforeOpen()
      if (!proceed) return
    }
    _setIsOpen(true)
  }
  const closeDialog = () => {
    if (disableClose) {
      return
    }
    _setIsOpen(false)
    if (onClose != null) {
      onClose()
    }
  }

  const DialogCloseButton = () => (
    <IconButton
      aria-label="close"
      className={styles.closeButton}
      onClick={closeDialog}
      disabled={disableClose}
    >
      <CloseIcon />
    </IconButton>
  )

  // control open state either from external prop or internal state
  // (prefer external prop, if provided)
  const open = isOpen != null ? isOpen : _isOpen
  const paperClassName = [classes?.paper, styles.dialogPaper]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      {trigger && <trigger.type {...trigger.props} onClick={openDialog} />}
      <MaterialDialog
        open={open}
        onClose={closeDialog}
        disableBackdropClick={disableClose}
        disableEscapeKeyDown={disableClose}
        disableEnforceFocus={disableEnforceFocus}
        fullWidth={fullWidth}
        fullScreen={fullScreen}
        maxWidth={maxWidth}
        hideBackdrop={hideBackdrop}
        scroll={scroll}
        aria-labelledby={ariaLabelledBy}
        classes={{
          ...classes,
          paper: paperClassName || styles.dialogPaper,
        }}
      >
        <>
          {showCloseButton ? <DialogCloseButton /> : null}
          {children({ closeDialog })}
        </>
      </MaterialDialog>
    </>
  )
}

Dialog.propTypes = {
  children: PropTypes.func.isRequired,
  trigger: PropTypes.element,
  isOpen: PropTypes.bool,
  fullWidth: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  disableClose: PropTypes.bool,
  maxWidth: PropTypes.string,
  onClose: PropTypes.func,
  beforeOpen: PropTypes.func,
  hideBackdrop: PropTypes.bool,
  classes: PropTypes.object,
  disableEnforceFocus: PropTypes.bool,
  scroll: PropTypes.oneOf(['body', 'paper']),
  'aria-labelledby': PropTypes.string,
}

Dialog.defaultProps = {
  fullWidth: false,
  showCloseButton: false,
  maxWidth: 'sm',
}

export default Dialog

const useActionStyles = makeStyles(theme => ({
  actions: {
    justifyContent: 'flex-start',
  },
}))

export function DialogActions(props) {
  const classes = useActionStyles()

  return <MaterialDialogActions {...props} className={classes.actions} />
}
